import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatDate } from "../../../lib/date";
import { quoteEmailPrefill } from "../../../lib/quoteEmail";
import {
  FileText, Plus, Send, Download, Link2, Trash2, Loader2, Pencil, X, Check, Copy,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { formatMoney } from "../../../lib/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLINGS = [
  { value: "one_off", label: "One-off" },
  { value: "monthly", label: "Per month" },
  { value: "yearly", label: "Per year" },
];
const BILLING_SUFFIX = { one_off: "", monthly: " /mo", yearly: " /yr" };

const STATUS_TONE = {
  draft: "neutral", sent: "warning", accepted: "success", declined: "overdue", expired: "neutral",
};

const money = (v, cur = "AUD") => formatMoney(v, cur);
const contactLabel = (c) => [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "Contact";

function StatusPill({ status }) {
  return (
    <span className={`ds-pill ${STATUS_TONE[status] || "neutral"}`} data-testid={`quote-status-${status}`}>
      <span className="dot" /> {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

function TotalsSummary({ totals }) {
  if (!totals) return null;
  const parts = [];
  for (const g of ["one_off", "monthly", "yearly"]) {
    if (totals[g]?.count) parts.push(`${money(totals[g].total)}${BILLING_SUFFIX[g]}`);
  }
  return <span className="mono text-[11.5px] text-[#1A1A1A]">{parts.join("  ·  ") || "—"}</span>;
}

// ── Builder dialog ──
export function QuoteBuilder({ open, onClose, catalogue, contacts, linkType, linkId, linkName, editingQuote, onSaved, settings = {} }) {
  const primary = contacts?.[0];
  const emptyForm = () => ({
    contactIdx: contacts?.length ? "0" : "",
    line_items: [],
    notes: "",
    terms: settings.default_terms || "Prices are in AUD and include GST at 10%. This quote is valid until the date shown. Acceptance may be completed online.",
    valid_until: new Date(Date.now() + (Number(settings.default_valid_days) || 30) * 86400000).toISOString().slice(0, 10),
  });
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [addCode, setAddCode] = useState("");

  useEffect(() => {
    if (!open) return;
    if (editingQuote) {
      const idx = contacts?.findIndex((c) => c.email === editingQuote.contact_email || contactLabel(c) === editingQuote.contact_name);
      setForm({
        contactIdx: idx >= 0 ? String(idx) : (contacts?.length ? "0" : ""),
        line_items: (editingQuote.line_items || []).map((l) => ({ ...l })),
        notes: editingQuote.notes || "",
        terms: editingQuote.terms || "",
        valid_until: editingQuote.valid_until || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      });
    } else {
      setForm(emptyForm());
    }
    setAddCode("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingQuote]);

  const addLine = (code) => {
    const cat = catalogue.find((c) => c.code === code);
    const line = cat
      ? { code: cat.code, description: cat.name, qty: 1, unit_price: (cat.prices || {}).AUD ?? cat.default_price, billing: cat.default_billing }
      : { code: "", description: "", qty: 1, unit_price: 0, billing: "one_off" };
    setForm((f) => ({ ...f, line_items: [...f.line_items, line] }));
    setAddCode("");
  };
  const updateLine = (i, patch) => setForm((f) => ({ ...f, line_items: f.line_items.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  // live totals
  const groups = { one_off: 0, monthly: 0, yearly: 0 };
  form.line_items.forEach((l) => { groups[l.billing] = (groups[l.billing] || 0) + (Number(l.qty) || 0) * (Number(l.unit_price) || 0); });

  const save = async () => {
    if (!form.line_items.length) { toast.error("Add at least one line item"); return; }
    const contact = contacts?.[Number(form.contactIdx)] || null;
    if (linkType === "client" && contacts?.length && !contact) { toast.error("Select a contact"); return; }
    setSaving(true);
    try {
      const body = {
        line_items: form.line_items.map((l) => ({ ...l, qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0 })),
        notes: form.notes, terms: form.terms, valid_until: form.valid_until,
        contact_name: contact ? contactLabel(contact) : (editingQuote?.contact_name || null),
        contact_email: contact ? (contact.email || null) : (editingQuote?.contact_email || null),
        link_name: linkName,
      };
      let res;
      if (editingQuote) {
        res = await axios.put(`${API}/quotes/${editingQuote.id}`, body);
      } else {
        res = await axios.post(`${API}/quotes`, { ...body, link_type: linkType, link_id: linkId });
      }
      toast.success(editingQuote ? "Quote updated" : `Quote created · ${res.data.quote.quote_number}`);
      onSaved(res.data.quote);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <SheetContent side="right" className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] w-full sm:max-w-[640px] p-0 flex flex-col" data-testid="quote-builder">
        <SheetHeader className="px-6 py-4 border-b border-[#E6E2D9] shrink-0">
          <SheetTitle style={{ fontFamily: "Manrope, sans-serif" }}>{editingQuote ? `Edit quote ${editingQuote.quote_number}` : "New quote"}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#59554D]">Contact</label>
              {contacts?.length ? (
                <Select value={form.contactIdx} onValueChange={(v) => setForm({ ...form, contactIdx: v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="quote-contact"><SelectValue placeholder="Select a contact" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map((c, i) => <SelectItem key={i} value={String(i)}>{contactLabel(c)}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-[#C23A00] mt-1.5">Add a contact on this record first — quotes are emailed to a saved contact.</p>
              )}
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Valid until</label>
              <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="quote-valid-until" />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-[#59554D] font-semibold uppercase tracking-wide">Line items</label>
              <Select value={addCode} onValueChange={addLine}>
                <SelectTrigger className="h-8 w-[220px] bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-xs" data-testid="quote-add-line"><SelectValue placeholder="+ Add from catalogue" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {catalogue.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name} — {money((c.prices || {}).AUD ?? c.default_price)}{BILLING_SUFFIX[c.default_billing]}</SelectItem>)}
                  <SelectItem value="__custom">Custom line…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.line_items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#DAD5CA] p-4 text-center text-xs text-[#8A857A]" data-testid="quote-no-lines">No line items yet — add one from the catalogue.</div>
            ) : (
              <div className="space-y-1.5" data-testid="quote-lines">
                <div className="grid items-center gap-2 px-1 text-[10px] uppercase tracking-wide text-[#8A857A]" style={{ gridTemplateColumns: "1fr 56px 90px 110px 28px" }}>
                  <span>Description</span><span className="text-right">Qty</span><span className="text-right">Unit</span><span>Billing</span><span />
                </div>
                {form.line_items.map((l, i) => (
                  <div key={i} className="grid items-center gap-2" style={{ gridTemplateColumns: "1fr 56px 90px 110px 28px" }} data-testid={`quote-line-${i}`}>
                    <Input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} className="h-8 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-sm" placeholder="Description" />
                    <Input type="number" value={l.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} className="h-8 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-sm text-right" />
                    <Input type="number" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} className="h-8 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-sm text-right" />
                    <Select value={l.billing} onValueChange={(v) => updateLine(i, { billing: v })}>
                      <SelectTrigger className="h-8 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{BILLINGS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <button type="button" onClick={() => removeLine(i)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#8A857A] hover:bg-[#FFE9E0] hover:text-[#C23A00]" data-testid={`quote-line-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            {/* live totals */}
            {form.line_items.length > 0 && (
              <div className="mt-2 rounded-lg bg-[#F6F4EF] border border-[#E6E2D9] p-2.5 flex flex-wrap gap-x-6 gap-y-1 justify-end" data-testid="quote-totals">
                {["one_off", "monthly", "yearly"].map((g) => groups[g] ? (
                  <span key={g} className="text-[11.5px] text-[#59554D]">
                    {BILLINGS.find((b) => b.value === g).label}: <span className="mono text-[#1A1A1A] font-semibold">{money(groups[g] * 1.1)}</span> <span className="text-[#8A857A]">incl GST{BILLING_SUFFIX[g]}</span>
                  </span>
                ) : null)}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs text-[#59554D]">Notes (shown on the quote)</label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="quote-notes" />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs text-[#59554D]">Terms &amp; conditions</label>
                {(settings.terms_templates?.length > 0) && (
                  <Select onValueChange={(v) => {
                    const body = v === "__default__"
                      ? (settings.default_terms || "")
                      : (settings.terms_templates.find((t) => t.id === v)?.body || "");
                    setForm((f) => ({ ...f, terms: body }));
                  }}>
                    <SelectTrigger className="h-7 w-[190px] bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-xs" data-testid="quote-terms-template"><SelectValue placeholder="Use a saved set…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">Default</SelectItem>
                      {settings.terms_templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} rows={2} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] mt-1" data-testid="quote-terms" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E6E2D9] flex items-center justify-end gap-2 shrink-0">
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-[#59554D] hover:text-[#1A1A1A]" data-testid="quote-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="quote-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} {editingQuote ? "Save changes" : "Create quote"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Panel embedded on client/prospect sheets ──
export default function QuotesPanel({ linkType, linkId, linkName, contacts = [] }) {
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState([]);
  const [settings, setSettings] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, q] = await Promise.all([
        axios.get(`${API}/quotes/catalogue`),
        axios.get(`${API}/quotes`, { params: { link_type: linkType, link_id: linkId } }),
      ]);
      setCatalogue(c.data.catalogue || []);
      setQuotes(q.data.quotes || []);
      axios.get(`${API}/quotes/settings`).then((s) => setSettings(s.data.settings || {})).catch(() => {});
    } catch (e) {
      // silent — panel is secondary
    } finally {
      setLoading(false);
    }
  }, [linkType, linkId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditingQuote(null); setBuilderOpen(true); };
  const openEdit = (q) => { setEditingQuote(q); setBuilderOpen(true); };

  const sendQuote = (q) => {
    const route = quoteEmailPrefill(q, { linkType, linkId });
    if (!route) { toast.error("This quote isn't linked to a client or prospect"); return; }
    navigate(route.dest, { state: route.state });
  };

  const downloadPdf = async (q) => {
    try {
      const res = await axios.get(`${API}/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quote_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const copyLink = (q) => {
    const link = q.accept_url || "";
    navigator.clipboard.writeText(link);
    toast.success("Public link copied");
  };

  const del = async () => {
    const q = deleteTarget;
    if (!q) return;
    setDeleting(true);
    try { await axios.delete(`${API}/quotes/${q.id}`); toast.success("Quote deleted"); setDeleteTarget(null); load(); }
    catch (e) { toast.error("Failed to delete"); }
    finally { setDeleting(false); }
  };

  const duplicate = async (q) => {
    setBusyId(q.id);
    try {
      const r = await axios.post(`${API}/quotes/${q.id}/duplicate`);
      toast.success(`Duplicated → ${r.data.quote.quote_number}`);
      setEditingQuote(r.data.quote);
      setBuilderOpen(true);
      load();
    } catch (e) { toast.error("Failed to duplicate"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-4 space-y-3" data-testid="quotes-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-[#8A6D00]" /> Quotes ({quotes.length})
        </div>
        <Button size="sm" onClick={openNew} className="h-8 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] text-xs" data-testid="quotes-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New quote
        </Button>
      </div>

      {loading ? (
        <div className="space-y-1.5">{[0, 1].map((i) => <div key={i} className="skeleton h-12 rounded-lg" />)}</div>
      ) : quotes.length === 0 ? (
        <p className="text-xs text-[#8A857A]">No quotes yet. Create one to send products, terms and pricing for online acceptance.</p>
      ) : (
        <div className="space-y-1.5">
          {quotes.map((q) => (
            <div key={q.id} className="rounded-lg border border-[#E6E2D9] bg-[#FBFAF7] px-3 py-2.5" data-testid={`quote-row-${q.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="mono text-[12px] font-semibold text-[#1A1A1A]">{q.quote_number}</span>
                  <StatusPill status={q.status} />
                </div>
                <TotalsSummary totals={q.totals} />
              </div>
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <span className="text-[11px] text-[#8A857A]">
                  {q.contact_name || "—"}{q.status === "accepted" && q.responded_by ? ` · accepted by ${q.responded_by}` : ""}{q.status === "declined" ? ` · declined` : ""} · valid to {q.valid_until ? formatDate(q.valid_until) : "—"}
                </span>
                <div className="flex items-center gap-0.5">
                  {q.status === "draft" && (
                    <button type="button" title="Edit" onClick={() => openEdit(q)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`quote-edit-${q.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  )}
                  <button type="button" title="Email to contact" onClick={() => sendQuote(q)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`quote-send-${q.id}`}><Send className="w-3.5 h-3.5" /></button>
                  <button type="button" title="Download PDF" onClick={() => downloadPdf(q)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`quote-pdf-${q.id}`}><Download className="w-3.5 h-3.5" /></button>
                  <button type="button" title="Duplicate" onClick={() => duplicate(q)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`quote-duplicate-${q.id}`}><Copy className="w-3.5 h-3.5" /></button>
                  <button type="button" title="Copy public link" onClick={() => copyLink(q)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`quote-link-${q.id}`}><Link2 className="w-3.5 h-3.5" /></button>
                  <button type="button" title="Delete" onClick={() => setDeleteTarget(q)} className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#FFE9E0] hover:text-[#C23A00]" data-testid={`quote-delete-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <QuoteBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        catalogue={catalogue}
        contacts={contacts}
        linkType={linkType}
        linkId={linkId}
        linkName={linkName}
        editingQuote={editingQuote}
        onSaved={() => load()}
        settings={settings}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="bg-white border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="quote-delete-confirm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trash2 className="w-4 h-4 text-[#C23A00]" /> Delete this quote?</DialogTitle></DialogHeader>
          {deleteTarget && (
            <p className="text-sm text-[#59554D]">Quote <span className="mono font-semibold text-[#1A1A1A]">{deleteTarget.quote_number}</span> will be permanently deleted. This can’t be undone.</p>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting} className="text-[#59554D] hover:text-[#1A1A1A]" data-testid="quote-delete-cancel">Cancel</Button>
            <Button onClick={del} disabled={deleting} className="bg-[#C23A00] hover:bg-[#A83100] text-white" data-testid="quote-delete-confirm-btn">{deleting && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
